/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { createContext, useContext, useEffect, useReducer } from 'react';
import { WithChildren } from 'types/component';
import { GASeatsPartial } from 'types/seats';
import { Pricing } from 'api/responseTypes';
import {
  CART_ACTION,
  CART_STORAGE_KEY,
  cartReducer,
  loadCartFromStorage,
  stateToSnapshot,
} from './helpers';
import { CartInfo, CartState, SelectedTimeslotInfo } from './types';

const emptyCart: CartInfo = { tickets: [] };

export const CartContext = createContext<CartState>(emptyCart);

export const CartContextProvider = ({ children }: WithChildren) => {
  const [state, dispatch] = useReducer(cartReducer, undefined, loadCartFromStorage);

  useEffect(() => {
    const snapshot = stateToSnapshot(state);
    if (snapshot) {
      sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(snapshot));
    } else {
      sessionStorage.removeItem(CART_STORAGE_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.eventKey,
    state.eventId,
    state.eventInfoId,
    state.holdToken,
    state.promoCode,
    state.discountAmount,
    state.tickets,
    state.invitationId,
    state.selectedTimeslot,
  ]);

  return <CartContext.Provider value={{ ...state, dispatch }}>{children}</CartContext.Provider>;
};

export const useCartContext = () => {
  const context = useContext(CartContext);

  if (!context || !context.dispatch) {
    throw new Error('Cart context has not been initialized!');
  }

  const clearCart = () => {
    context.dispatch!({ type: CART_ACTION.CLEAR_TICKETS });
  };

  const clearData = () => {
    context.dispatch!({ type: CART_ACTION.CLEAR_DATA });
  };

  const setMatchId = (eventInfoId: number) => {
    context.dispatch!({ type: CART_ACTION.SET_MATCH_ID, payload: { eventInfoId } });
  };

  const setEventId = (eventId: number) => {
    context.dispatch!({ type: CART_ACTION.SET_EVENT_ID, payload: { eventId } });
  };

  const setEventKey = (eventKey: string) => {
    context.dispatch!({ type: CART_ACTION.SET_EVENT_KEY, payload: { eventKey } });
  };

  const setHoldToken = (holdToken: string) => {
    context.dispatch!({ type: CART_ACTION.SET_HOLD_TOKEN, payload: { holdToken } });
  };

  const updateTickets = (ga: GASeatsPartial, apiPricing: Pricing[]) => {
    context.dispatch!({ type: CART_ACTION.UPDATE_TICKETS, payload: { ga, apiPricing } });
  };

  const setPromoCode = (promoCode: string, discountAmount: number) => {
    context.dispatch!({
      type: CART_ACTION.SET_PROMO_CODE,
      payload: { promoCode, discountAmount },
    });
  };

  const clearPromoCode = () => {
    context.dispatch!({ type: CART_ACTION.CLEAR_PROMO_CODE });
  };

  const setInvitationId = (invitationId: string) => {
    context.dispatch!({ type: CART_ACTION.SET_INVITATION_ID, payload: { invitationId } });
  };

  const setTimeslot = (selectedTimeslot: SelectedTimeslotInfo | null) => {
    context.dispatch!({ type: CART_ACTION.SET_TIMESLOT, payload: { selectedTimeslot } });
  };

  return {
    clearCart,
    clearData,
    setMatchId,
    setEventId,
    setEventKey,
    setHoldToken,
    updateTickets,
    setPromoCode,
    clearPromoCode,
    setInvitationId,
    setTimeslot,
    tickets: context.tickets,
    eventInfoId: context.eventInfoId,
    eventId: context.eventId,
    eventKey: context.eventKey,
    holdToken: context.holdToken,
    promoCode: context.promoCode,
    discountAmount: context.discountAmount,
    invitationId: context.invitationId,
    selectedTimeslot: context.selectedTimeslot,
  };
};
